/**
 * AmenagementEntreprise.jsx
 * Page publique — validation de l'aménagement d'horaires par l'employeur
 * Accessible via /amenagement-entreprise/:token (sans authentification)
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function AmenagementEntreprise() {
  const { token } = useParams();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [step,    setStep]    = useState('lecture'); // 'lecture' | 'valider' | 'refuser' | 'done'
  const [validateurNom, setValidateurNom] = useState('');
  const [motifRefus,    setMotifRefus]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [doneMsg,    setDoneMsg]    = useState('');

  useEffect(() => {
    fetch(`${API}/amenagement-entreprise/${token}`)
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e.error || 'Erreur')))
      .then(d => setData(d))
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [token]);

  async function handleValider() {
    if (!validateurNom.trim()) { alert('Veuillez indiquer votre nom.'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/amenagement-entreprise/${token}/valider`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ validateur_nom: validateurNom.trim() }),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
      setStep('done');
      setDoneMsg('✅ Aménagement validé. Merci.');
    } catch (e) { alert(e.message); }
    finally { setSubmitting(false); }
  }

  async function handleRefuser() {
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/amenagement-entreprise/${token}/refuser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motif_refus: motifRefus.trim() }),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Erreur');
      setStep('done');
      setDoneMsg('Refus enregistré. Nous avons bien pris note de votre décision.');
    } catch (e) { alert(e.message); }
    finally { setSubmitting(false); }
  }

  // ── Loaders / erreurs ──
  if (loading) return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <p style={{ textAlign: 'center', color: '#6b7280' }}>⏳ Chargement…</p>
      </div>
    </div>
  );

  if (error) return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
          <h2 style={{ color: '#1f2937', marginBottom: 8 }}>Lien non disponible</h2>
          <p style={{ color: '#6b7280', fontSize: 14 }}>{error}</p>
        </div>
      </div>
    </div>
  );

  const { amenagement, stagiaires } = data;
  const dejaTraite = amenagement.statut_global === 'Validé' || amenagement.statut_global === 'Refusé';

  // ── Fait ──
  if (step === 'done') return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>
            {doneMsg.startsWith('✅') ? '✅' : '📋'}
          </div>
          <h2 style={{ color: '#1f2937', marginBottom: 12 }}>Réponse enregistrée</h2>
          <p style={{ color: '#374151', fontSize: 15, lineHeight: 1.6 }}>{doneMsg}</p>
        </div>
      </div>
    </div>
  );

  return (
    <div style={pageStyle}>
      {/* En-tête CEJ */}
      <div style={headerStyle}>
        <div style={{ fontSize: 11, opacity: 0.8, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
          CEJ Concept — Formation professionnelle
        </div>
        <h1 style={{ fontSize: 18, fontWeight: 800, margin: 0, lineHeight: 1.3 }}>
          Aménagement d'horaires
        </h1>
        <p style={{ fontSize: 13, opacity: 0.9, marginTop: 4, marginBottom: 0 }}>
          Demande de validation — Employeur
        </p>
      </div>

      <div style={{ padding: '0 16px 32px' }}>

        {/* Carte formation */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>📋 Formation concernée</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <tbody>
              <tr style={trStyle}><td style={tdLabel}>Formation</td><td style={tdVal}>{amenagement.intitule_formation || '—'}</td></tr>
              <tr style={trStyle}><td style={tdLabel}>Dates</td>
                <td style={tdVal}>
                  {amenagement.date_debut ? `Du ${fmtDate(amenagement.date_debut)} au ${fmtDate(amenagement.date_fin)}` : '—'}
                </td>
              </tr>
              {amenagement.lieu && <tr style={trStyle}><td style={tdLabel}>Lieu</td><td style={tdVal}>{amenagement.lieu}</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Carte aménagement */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>🕐 Aménagement demandé</h3>
          <div style={{ background: '#fef9f0', border: '1px solid #fed7aa', borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', marginBottom: 6 }}>Motif</div>
            <p style={{ margin: 0, fontSize: 14, color: '#1f2937', lineHeight: 1.6 }}>{amenagement.motif}</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
                Horaires initiaux
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#374151' }}>
                {amenagement.horaires_originaux || '—'}
              </div>
            </div>
            <div style={{ background: '#dbeafe', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 11, color: '#1d4ed8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
                Nouveaux horaires
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1d4ed8' }}>
                {amenagement.nouveaux_horaires}
              </div>
            </div>
          </div>
        </div>

        {/* Stagiaires concernés */}
        <div style={cardStyle}>
          <h3 style={sectionTitle}>👥 Stagiaires concernés</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {stagiaires.map(s => (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', background: '#f9fafb', borderRadius: 8,
                border: '1px solid #e5e7eb',
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1f2937' }}>{s.nom_stagiaire}</div>
                  {s.nom_client && <div style={{ fontSize: 12, color: '#6b7280' }}>{s.nom_client}</div>}
                </div>
                {s.statut_entreprise === 'Validé' && (
                  <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 700 }}>✓ Validé</span>
                )}
                {s.statut_entreprise === 'Refusé' && (
                  <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 700 }}>✗ Refusé</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Mentions légales */}
        <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, margin: '0 4px 16px', fontStyle: 'italic' }}>
          À la demande des stagiaires et avec l'accord des employeurs concernés, les horaires de la
          formation sont aménagés comme indiqué ci-dessus. La feuille d'émargement reflétera les
          horaires réellement réalisés.
        </div>

        {/* Actions */}
        {!dejaTraite ? (
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Votre réponse</h3>
            <p style={{ fontSize: 13, color: '#374151', marginBottom: 12 }}>
              En validant, vous confirmez avoir pris connaissance de cet aménagement et donnez votre accord.
            </p>
            <label style={labelStyle}>Votre nom et fonction *</label>
            <input
              type="text"
              value={validateurNom}
              onChange={e => setValidateurNom(e.target.value)}
              placeholder="Ex : M. Dupont, Responsable RH"
              style={inputStyle}
            />
            {step === 'refuser' && (
              <>
                <label style={{ ...labelStyle, marginTop: 12 }}>Motif du refus (optionnel)</label>
                <textarea
                  value={motifRefus}
                  onChange={e => setMotifRefus(e.target.value)}
                  placeholder="Expliquez votre refus…"
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button
                onClick={handleValider}
                disabled={submitting || !validateurNom.trim()}
                style={btnStyle('#16a34a')}>
                {submitting && step !== 'refuser' ? '⏳…' : '✓ Valider l\'aménagement'}
              </button>
              {step !== 'refuser' ? (
                <button
                  onClick={() => setStep('refuser')}
                  disabled={submitting}
                  style={{ ...btnStyle('#dc2626'), flex: '0 0 auto', width: 'auto', padding: '12px 16px' }}>
                  ✗ Refuser
                </button>
              ) : (
                <button
                  onClick={handleRefuser}
                  disabled={submitting}
                  style={{ ...btnStyle('#dc2626'), flex: '0 0 auto', width: 'auto', padding: '12px 16px' }}>
                  {submitting ? '⏳…' : '✗ Confirmer le refus'}
                </button>
              )}
            </div>
            {step === 'refuser' && (
              <button onClick={() => setStep('lecture')} style={{ ...btnStyle('#6b7280', 0.7), marginTop: 8 }}>
                ← Annuler
              </button>
            )}
          </div>
        ) : (
          <div style={{
            background: amenagement.statut_global === 'Validé' ? '#d1fae5' : '#fee2e2',
            border: `1px solid ${amenagement.statut_global === 'Validé' ? '#6ee7b7' : '#fca5a5'}`,
            borderRadius: 10, padding: '14px 16px', textAlign: 'center', fontSize: 14,
            color: amenagement.statut_global === 'Validé' ? '#065f46' : '#991b1b',
            fontWeight: 600,
          }}>
            {amenagement.statut_global === 'Validé'
              ? '✅ Aménagement déjà validé'
              : '✗ Aménagement déjà refusé'}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const pageStyle = {
  minHeight: '100vh', background: '#f3f4f6',
  fontFamily: "'Segoe UI', system-ui, sans-serif",
};
const headerStyle = {
  background: 'linear-gradient(135deg, #1a3668, #2563eb)',
  color: '#fff', padding: '20px 20px 18px',
};
const cardStyle = {
  background: '#fff', borderRadius: 12, padding: '16px',
  boxShadow: '0 1px 4px rgba(0,0,0,.08)', marginBottom: 14, marginTop: 14,
};
const sectionTitle = {
  fontSize: 14, fontWeight: 700, color: '#1f2937',
  margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.04em',
};
const trStyle = { borderBottom: '1px solid #f3f4f6' };
const tdLabel = { padding: '7px 8px 7px 0', fontSize: 12, color: '#6b7280', fontWeight: 600, width: '38%', verticalAlign: 'top' };
const tdVal = { padding: '7px 0', fontSize: 13, color: '#1f2937', verticalAlign: 'top' };
const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box',
};
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const btnStyle = (bg, opacity = 1) => ({
  background: bg, color: '#fff', border: 'none', borderRadius: 8,
  padding: '12px 16px', fontSize: 15, fontWeight: 700, cursor: opacity < 1 ? 'default' : 'pointer',
  flex: 1, opacity, width: '100%',
});
